import * as stylex from "@stylexjs/stylex";
import {
  colors,
  fonts,
  fontSizes,
  spacing,
} from "@softmax/design-system/tokens.stylex";
import type { StoreDiagramFeatured } from "./play-diagram-records";

export function Scoreboard({
  featured,
  scoreLabel,
}: {
  readonly featured: StoreDiagramFeatured | null;
  readonly scoreLabel: string;
}) {
  if (!featured)
    return <p {...stylex.props(styles.empty)}>No rounds played yet</p>;

  return (
    <div {...stylex.props(styles.scoreboard)}>
      <p {...stylex.props(styles.resultTitle)}>
        {featured.name}&rsquo;s results
      </p>
      <div aria-hidden="true" {...stylex.props(styles.rule)} />
      <dl {...stylex.props(styles.facts)}>
        <dt {...stylex.props(styles.factLabel)}>Rank</dt>
        <dd {...stylex.props(styles.factValue)}>{featured.rank}</dd>
        <dt {...stylex.props(styles.factLabel)}>{scoreLabel}</dt>
        <dd {...stylex.props(styles.factValue)}>{featured.score}</dd>
        <dt {...stylex.props(styles.factLabel)}>Rounds</dt>
        <dd {...stylex.props(styles.factValue)}>
          {featured.roundsPlayed} played
        </dd>
        {featured.lastRound ? (
          <>
            <dt {...stylex.props(styles.factLabel)}>Last round</dt>
            <dd {...stylex.props(styles.factValue)}>{featured.lastRound}</dd>
          </>
        ) : null}
      </dl>
    </div>
  );
}

const styles = stylex.create({
  empty: {
    margin: 0,
    textAlign: "center",
    fontFamily: fonts.sans,
    fontSize: fontSizes.caption,
    lineHeight: "15px",
    color: colors.muted,
    fontStyle: "italic",
  },
  scoreboard: {
    display: "flex",
    width: "100%",
    height: "100%",
    flexDirection: "column",
    justifyContent: "center",
    gap: spacing.space8,
    overflow: "hidden",
  },
  resultTitle: {
    margin: 0,
    fontFamily: fonts.mono,
    fontSize: {
      default: fontSizes.caption,
      "@container (min-width: 560px)": fontSizes.small,
    },
    lineHeight: { default: "16px", "@container (min-width: 560px)": "18px" },
    color: colors.muted,
  },
  rule: { height: 1, width: "100%", backgroundColor: colors.border },
  facts: {
    margin: 0,
    display: "grid",
    gridTemplateColumns: "88px minmax(0, 1fr)",
    alignItems: "baseline",
    columnGap: spacing.space10,
    rowGap: spacing.space8,
    fontFamily: fonts.mono,
    fontSize: {
      default: fontSizes.small,
      "@container (min-width: 560px)": fontSizes.caption,
    },
    lineHeight: { default: "18px", "@container (min-width: 560px)": "16px" },
  },
  factLabel: { color: colors.muted },
  factValue: {
    margin: 0,
    minWidth: 0,
    color: colors.foreground,
    overflowWrap: "anywhere",
  },
});
